// src/components/dashboard/DeregisteredAthletesStats.tsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import GroupsIcon from "@mui/icons-material/Groups";
import EmailIcon from "@mui/icons-material/Email";
import DownloadIcon from "@mui/icons-material/Download";
import BarChartIcon from "@mui/icons-material/BarChart";
import UndoIcon from "@mui/icons-material/Undo";
import { Link } from "react-router-dom";
import axios from "../../utils/axiosConfig";

import logger from '../../utils/logger';

interface DeregisteredAthlete {
  id: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
  competitionGroup: string;
  competitionGroupName: string;
  deregisteredAt: string;
  reason: string;
}

interface ClubDeregistrations {
  clubId: string;
  clubName: string;
  deregisteredAthletes: DeregisteredAthlete[];
}

interface DeregisteredAthletesStatsProps {
  competitionId: string;
}

const DeregisteredAthletesStats: React.FC<DeregisteredAthletesStatsProps> = ({ competitionId }) => {
    const [expanded, setExpanded] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [deregistrationData, setDeregistrationData] = useState<ClubDeregistrations[]>([]);
    const [totalDeregistered, setTotalDeregistered] = useState(0);
    const [reregisterDialogOpen, setReregisterDialogOpen] = useState(false);
    const [selectedDeregistration, setSelectedDeregistration] = useState<{ id: string; name: string } | null>(null);
    const [reregisterLoading, setReregisterLoading] = useState(false);
    const [reregisterError, setReregisterError] = useState<string | null>(null);

    const handleExpandToggle = () => {
        setExpanded(!expanded);
    };

    const fetchDeregisteredAthletes = async () => {
        if (!competitionId) return;
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/deregistrations/${competitionId}`);
            setDeregistrationData(response.data.clubs);
            setTotalDeregistered(response.data.totalDeregistered);
        } catch (err) {
            logger.error('Error fetching deregistered athletes data', {error: err});
            setError('Fehler beim Laden der Abmeldungen');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDeregisteredAthletes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionId]);

    const openReregisterDialog = (id: string, firstname: string, surname: string) => {
        setSelectedDeregistration({ id, name: `${firstname} ${surname}` });
        setReregisterError(null);
        setReregisterDialogOpen(true);
    };

    const handleReregisterConfirm = async () => {
        if (!selectedDeregistration) return;
        setReregisterLoading(true);
        setReregisterError(null);
        try {
            await axios.post(`/api/admin/deregistrations/${selectedDeregistration.id}/reregister`);
            setReregisterDialogOpen(false);
            setSelectedDeregistration(null);
            await fetchDeregisteredAthletes();
        } catch (err: any) {
            logger.error('Error reregistering athlete', {error: err});
            setReregisterError(err?.response?.data?.message || 'Fehler beim Rückgängigmachen der Abmeldung');
        } finally {
            setReregisterLoading(false);
        }
    };

    const getGenderLabel = (gender: string) => {
        switch (gender) {
            case 'm': return 'männlich';
            case 'w': return 'weiblich';
            default: return gender;
        }
    };

    // Group athletes by competition class within each club
    const getGroupedAthletes = (athletes: DeregisteredAthlete[]) => {
        const grouped: { [key: string]: DeregisteredAthlete[] } = {};
        
        athletes.forEach(athlete => {
            const groupKey = athlete.competitionGroupName;
            if (!grouped[groupKey]) {
                grouped[groupKey] = [];
            }
            grouped[groupKey].push(athlete);
        });
        
        return grouped;
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" my={2}>
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!deregistrationData || deregistrationData.length === 0 || totalDeregistered === 0) {
        return (
            <Box sx={{ 
                my: 3, 
                p: 2, 
                backgroundColor: '#e3f2fd',
                borderRadius: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonOffIcon color="info" />
                    <Typography variant="h6">
                        Keine abgemeldeten Teilnehmer
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ my: 3 }}>
            <Accordion 
                expanded={expanded} 
                onChange={handleExpandToggle}
                sx={{ boxShadow: 1 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: '#e3f2fd', // Light blue background
                        '&:hover': {
                            backgroundColor: '#bbdefb',
                        },
                    }}
                >
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonOffIcon color="info" />
                            <Typography variant="h6">
                                Abgemeldete Teilnehmer
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                                icon={<GroupsIcon />}
                                label={`${deregistrationData.length} Vereine`}
                                color="primary"
                                size="small"
                            />
                            <Chip
                                icon={<PersonOffIcon />}
                                label={`${totalDeregistered} Athleten`}
                                color="info"
                                size="small"
                            />
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {deregistrationData.map((club) => (
                        <Accordion key={club.clubId}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    width: '100%',
                                    pr: 2
                                }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {club.clubName}
                                    </Typography>
                                    <Chip
                                        color="info"
                                        size="small"
                                        label={`${club.deregisteredAthletes.length} Abmeldungen`}
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {Object.entries(getGroupedAthletes(club.deregisteredAthletes)).map(([groupName, athletes]) => (
                                    <Box key={groupName} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', backgroundColor: '#f5f5f5', p: 1 }}>
                                            Wettkampfklasse: {groupName}
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Geschlecht</TableCell>
                                                        <TableCell>Jahrgang</TableCell>
                                                        <TableCell>Abmeldedatum</TableCell>
                                                        <TableCell>Grund</TableCell>
                                                        <TableCell></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {athletes.map((athlete) => (
                                                        <TableRow key={athlete.id}>
                                                            <TableCell>{athlete.firstname} {athlete.surname}</TableCell>
                                                            <TableCell>{getGenderLabel(athlete.gender)}</TableCell>
                                                            <TableCell>{athlete.age}</TableCell>
                                                            <TableCell>{formatDate(athlete.deregisteredAt)}</TableCell>
                                                            <TableCell>{athlete.reason || '-'}</TableCell>
                                                            <TableCell>
                                                                <Tooltip title="Abmeldung rückgängig machen">
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="success"
                                                                        startIcon={<UndoIcon />}
                                                                        onClick={() => openReregisterDialog(athlete.id, athlete.firstname, athlete.surname)}
                                                                    >
                                                                        Rückgängig
                                                                    </Button>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </AccordionDetails>
            </Accordion>

            <Dialog open={reregisterDialogOpen} onClose={() => !reregisterLoading && setReregisterDialogOpen(false)}>
                <DialogTitle>Abmeldung rückgängig machen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Soll die Abmeldung von <strong>{selectedDeregistration?.name}</strong> wirklich rückgängig gemacht werden? Der Sportler wird wieder als gemeldet eingetragen.
                    </DialogContentText>
                    {reregisterError && (
                        <Alert severity="error" sx={{ mt: 2 }}>{reregisterError}</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReregisterDialogOpen(false)} disabled={reregisterLoading}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleReregisterConfirm}
                        color="success"
                        variant="contained"
                        disabled={reregisterLoading}
                        startIcon={reregisterLoading ? <CircularProgress size={16} /> : <UndoIcon />}
                    >
                        Rückgängig machen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DeregisteredAthletesStats;